import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { toast } from 'react-toastify';
import * as api from './api';
import type { ListVarietiesParams, VarietyFormValues } from './schema';

const ROOT_KEY = 'varieties';
const listKey = (params: ListVarietiesParams) =>
  [ROOT_KEY, 'list', params] as const;
const detailKey = (id: string) => [ROOT_KEY, 'detail', id] as const;
const optionsKey = [ROOT_KEY, 'options'] as const;

function describeError(err: unknown, fallback: string): string {
  if (isAxiosError(err)) {
    const data = err.response?.data as
      | { message?: string | string[]; error?: string }
      | undefined;
    const msg = data?.message;
    if (Array.isArray(msg)) return msg.join(', ');
    if (typeof msg === 'string' && msg.length > 0) return msg;
    if (data?.error) return data.error;
  }
  return fallback;
}

export function useVarieties(params: ListVarietiesParams) {
  return useQuery({
    queryKey: listKey(params),
    queryFn: () => api.listVarieties(params),
    placeholderData: (prev) => prev,
  });
}

export function useVariety(id: string | null) {
  return useQuery({
    queryKey: detailKey(id ?? ''),
    queryFn: () => api.getVariety(id as string),
    enabled: !!id,
  });
}

export function useVarietyOptions() {
  return useQuery({
    queryKey: optionsKey,
    queryFn: () => api.getVarietyOptions(),
    staleTime: 60 * 60 * 1000,
  });
}

export function useCreateVariety() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (values: VarietyFormValues) => api.createVariety(values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ROOT_KEY, 'list'] });
      queryClient.invalidateQueries({ queryKey: optionsKey });
      toast.success('Variety created');
    },
    onError: (err) =>
      toast.error(describeError(err, 'Failed to create variety')),
  });
}

export function useUpdateVariety() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, values }: { id: string; values: VarietyFormValues }) =>
      api.updateVariety(id, values),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: [ROOT_KEY, 'list'] });
      queryClient.invalidateQueries({ queryKey: detailKey(updated.id) });
      queryClient.invalidateQueries({ queryKey: optionsKey });
      toast.success('Variety updated');
    },
    onError: (err) =>
      toast.error(describeError(err, 'Failed to update variety')),
  });
}
